type ClientErrorPayload = {
    message: string;
    stack?: string;
    source?: string;
    line?: number;
    column?: number;
    userAgent?: string;
    href?: string;
    createdAt?: unknown;
    sessionId?: string;
};

const loadFirestoreDeps = async () => {
    const [firestore, firebase] = await Promise.all([import('firebase/firestore'), import('../firebase')]);
    return { firestore, db: firebase.db, auth: firebase.auth };
};

const getSessionId = (): string | undefined => {
    if (typeof window === 'undefined' || !window.localStorage) return undefined;
    const key = 'client_error_session_id';

    try {
        const existing = localStorage.getItem(key);
        if (existing) return existing;
    } catch (error) {
        console.warn('Unable to read client error session id from localStorage.', error);
    }

    const sid = globalThis?.crypto?.randomUUID?.() ?? Math.random().toString(36).slice(2);

    try {
        localStorage.setItem(key, sid);
    } catch (error) {
        console.warn('Unable to persist client error session id in localStorage.', error);
    }

    return sid;
};

const withSession = (base: Omit<ClientErrorPayload, 'createdAt' | 'sessionId'>): ClientErrorPayload => ({
    ...base,
    sessionId: getSessionId(),
});

export const logClientError = async (payload: Omit<ClientErrorPayload, 'createdAt' | 'sessionId'>) => {
    const { firestore, db, auth } = await loadFirestoreDeps();
    if (!auth.currentUser) return;
    const entry = {
        ...withSession(payload),
        createdAt: firestore.serverTimestamp(),
    };
    await firestore.addDoc(firestore.collection(db, 'client_logs'), entry);
};

export const installClientErrorLogging = () => {
    if (typeof window === 'undefined') return;

    window.addEventListener('error', (event) => {
        const errorEvent = event as ErrorEvent;
        const payload = withSession({
            message: errorEvent.message || 'Unknown error',
            stack: errorEvent.error?.stack,
            source: errorEvent.filename,
            line: errorEvent.lineno,
            column: errorEvent.colno,
            userAgent: navigator?.userAgent,
            href: window.location?.href,
        });
        console.error('Captured window error', payload);
        logClientError({
            message: payload.message,
            stack: payload.stack,
            source: payload.source,
            line: payload.line,
            column: payload.column,
            userAgent: payload.userAgent,
            href: payload.href,
        }).catch((err) => console.warn('Failed to log client error', err));
    });

    window.addEventListener('unhandledrejection', (event) => {
        const reason = event.reason instanceof Error ? event.reason : undefined;
        const payload = withSession({
            message: reason?.message ?? 'Unhandled promise rejection',
            stack: reason?.stack,
            userAgent: navigator?.userAgent,
            href: window.location?.href,
        });
        console.error('Captured unhandled rejection', payload);
        logClientError({
            message: payload.message,
            stack: payload.stack,
            userAgent: payload.userAgent,
            href: payload.href,
        }).catch((err) => console.warn('Failed to log client error', err));
    });
};
